import React, { useCallback, useMemo } from 'react';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { StyledDivider } from '../../../RegistryDialog/DialogFooter.styles';
import { InSummaryItem } from '../../../ShoppingCart/components/SummaryItemList/InSummaryItem';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { MonetaryValue, useExpressCheckoutContext } from '../../ExpressCheckout.provider';
import { useCurrencyContext } from '@shared/utils/currency';
import { v4 as uuid } from 'uuid';

export const PriceLine = ({ label, price, bold }: { label: string; price: string | number; bold?: boolean }) => (
  <Flex justifyContent={'space-between'}>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {label}
    </TextV2>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {price}
    </TextV2>
  </Flex>
);

export const PaymentSummary = () => {
  const { t } = useTranslation('sharedRegistry');
  const { subtotal, total, salesTax, delivery, freeDeliveryPriceText, pendingSalesTaxPriceText, eCard } = t('shoppingCart');
  const currencyFormatter = useCurrencyContext();
  const {
    state: { purchaseProducts, additionalCostLines, giftWrap, totalPrice, productsPrice, eCardPrice, currencyCode },
  } = useExpressCheckoutContext();

  const formatPrice = useCallback(
    (value: MonetaryValue) => currencyFormatter.formatCurrency(value.valueInMinorUnits / 100, { form: 'short', currency: currencyCode }),
    [currencyCode, currencyFormatter],
  );

  const formatPriceOrPending = (value?: MonetaryValue | null) => {
    return value ? formatPrice(value) : pendingSalesTaxPriceText();
  };
  const additionalCostPriceLines = useMemo(() => {
    let foundShippingLine = false;
    const priceLines = additionalCostLines.map(line => {
      const valueInMinorUnits = line.lineValue?.valueInMinorUnits;
      const isShippingLine = line.__typename === 'SalesOrderShippingAndHandlingLine';
      foundShippingLine = foundShippingLine || isShippingLine;

      let price = formatPrice(line.lineValue);
      if (valueInMinorUnits === 0) {
        if (isShippingLine) {
          price = freeDeliveryPriceText();
        }
      }
      return (
        <PriceLine
          key={`costLine-${uuid()}`}
          label={line.title}
          price={price}
        />
      );
    });
    if (!foundShippingLine) {
      // lack-of-a-line for Shipping means "it's free"
      // Shipping is displayed first
      priceLines.unshift(
        <PriceLine
          key={`costLine-${uuid()}`}
          label={delivery()}
          price={freeDeliveryPriceText()}
        />,
      );
    }
    return priceLines;
  }, [additionalCostLines, delivery, freeDeliveryPriceText, formatPrice]);

  return (
    <>
      <Box
        marginTop={pxToRem(-24)}
        marginRight={pxToRem(-16)}
      >
        {purchaseProducts.map(product => (
          <InSummaryItem
            key={product.registryItemId}
            inCartProduct={{ product, quantity: product.quantity, registryItemId: product.registryItemId }}
          />
        ))}
      </Box>
      <StyledDivider />
      <SpacingStack spacing={pxToRem(8)}>
        <PriceLine
          label={subtotal()}
          price={formatPrice(productsPrice)}
        />
        {additionalCostPriceLines}
        {giftWrap?.wrapOption === 'eCard' && (
          <PriceLine
            label={eCard()}
            price={formatPriceOrPending(eCardPrice)}
          />
        )}
        <PriceLine
          label={salesTax()}
          price={pendingSalesTaxPriceText()}
        />
      </SpacingStack>
      <StyledDivider />
      <PriceLine
        label={total()}
        price={formatPriceOrPending(totalPrice)}
        bold
      />
    </>
  );
};
